import { useRef, useState } from "react";
import { motion, useScroll, useTransform, useMotionValueEvent } from "framer-motion";
import { useLang } from "@/App";
import { Overline } from "@/components/motion";

const SCENE_IMAGES = [
  "https://images.unsplash.com/photo-1533960056888-5166859c1b1d?crop=entropy&cs=srgb&fm=jpg&q=85",
  "https://images.unsplash.com/photo-1591029382559-3d2bd0c671df?crop=entropy&cs=srgb&fm=jpg&q=85",
  "https://images.unsplash.com/photo-1547929502-7d4f116f36ab?crop=entropy&cs=srgb&fm=jpg&q=85",
  "https://images.unsplash.com/photo-1789886700482-ddb3c7af301d?crop=entropy&cs=srgb&fm=jpg&q=85",
];


function Scene({ progress, index, total, scene, img }) {
  const slice = 1 / total;
  const s = index * slice;
  const e = s + slice;
  const inStart = index === 0 ? 0 : s;
  const inEnd = index === 0 ? 0.001 : s + slice * 0.22;
  const outStart = index === total - 1 ? 0.999 : e - slice * 0.18;
  const outEnd = index === total - 1 ? 1 : e;

  const opacity = useTransform(
    progress,
    [inStart, inEnd, outStart, outEnd],
    [index === 0 ? 1 : 0, 1, 1, index === total - 1 ? 1 : 0]
  );
  const scale = useTransform(progress, [s, e], [1.06, 1.26]);
  const x = useTransform(progress, [s, e], ["0%", index % 2 === 0 ? "-4%" : "4%"]);
  const capOpacity = useTransform(progress, [inEnd, inEnd + 0.03, outStart, outEnd], [0, 1, 1, index === total - 1 ? 1 : 0]);
  const capY = useTransform(progress, [inEnd, inEnd + 0.06], [44, 0]);

  return (
    <motion.div style={{ opacity }} className="absolute inset-0" data-testid={`story-scene-${index}`}>
      <motion.img
        src={img}
        alt={scene.title}
        style={{ scale, x }}
        className="absolute inset-0 h-full w-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/30 to-slate-950/60" aria-hidden="true" />
      <div className="grain-overlay absolute inset-0" aria-hidden="true" />
      <motion.div
        style={{ opacity: capOpacity, y: capY }}
        className="absolute bottom-20 left-5 max-w-xl sm:bottom-24 sm:left-12"
      >
        <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.28em] text-aqua">{scene.k}</p>
        <h3 className="mt-3 font-headline text-3xl font-extrabold tracking-tight text-bone sm:text-5xl">
          {scene.title}
        </h3>
        <p className="mt-4 max-w-md text-sm leading-relaxed text-bone/80 sm:text-base">{scene.desc}</p>
      </motion.div>
    </motion.div>
  );
}

export default function StoryFilm() {
  const { t } = useLang();
  const story = t.story;
  const ref = useRef(null);
  const [active, setActive] = useState(0);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  useMotionValueEvent(scrollYProgress, "change", (v) =>
    setActive(Math.min(story.scenes.length - 1, Math.max(0, Math.floor(v * story.scenes.length))))
  );

  return (
    <section data-testid="story-film" className="relative bg-slate-950">
      <div className="mx-auto max-w-7xl px-4 pb-10 pt-20 sm:px-6 sm:pt-24 lg:px-8">
        <Overline dark>{story.overline}</Overline>
        <h2 className="mt-4 max-w-3xl font-headline text-3xl font-extrabold tracking-tight text-bone sm:text-4xl lg:text-5xl">
          {story.title}
        </h2>
      </div>

      <div ref={ref} className="relative h-[380vh]">
        <div className="sticky top-0 h-screen overflow-hidden">
          {story.scenes.map((scene, i) => (
            <Scene
              key={i}
              progress={scrollYProgress}
              index={i}
              total={story.scenes.length}
              scene={scene}
              img={SCENE_IMAGES[i]}
            />
          ))}

          <div className="absolute right-5 top-1/2 hidden -translate-y-1/2 flex-col items-end gap-5 sm:right-10 md:flex">
            {story.scenes.map((scene, i) => (
              <div key={i} className="flex items-center gap-3">
                <span
                  className={`font-mono text-[10px] uppercase tracking-[0.22em] transition-colors duration-500 ${
                    active === i ? "text-aqua" : "text-bone/30"
                  }`}
                >
                  {scene.k}
                </span>
                <span
                  className={`h-1.5 rounded-full transition-all duration-500 ${
                    active === i ? "w-8 bg-aqua" : "w-1.5 bg-bone/25"
                  }`}
                />
              </div>
            ))}
          </div>

          <div className="absolute bottom-0 left-0 right-0 h-1 bg-bone/10" data-testid="story-progress">
            <motion.div style={{ scaleX: scrollYProgress }} className="h-full origin-left bg-aqua" />
          </div>
        </div>
      </div>
    </section>
  );
}
